import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonWalrus = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Python 3.8+</div>
                <h1>The Walrus Operator (:=)</h1>
                <p className="hero-lead">
                    Meet the operator that changed Python history. Known formally as "Assignment Expressions,"
                    the Walrus operator (<code>:=</code>) bridges the gap between statements and expressions,
                    enabling cleaner, faster, and more expressive codeif you know how to tame it.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    In the history of Python, no feature has been more controversial than PEP 572. The proposal to add
                    assignment expressions caused such a heated debate that it led to Guido van Rossum (Python's creator)
                    stepping down from his role as "Benevolent Dictator For Life" (BDFL).
                </p>
                <p>
                    Why the drama? For 30 years, Python strictly separated <strong>Statements</strong> (actions like <code>x = 5</code>)
                    from <strong>Expressions</strong> (values like <code>5 + 3</code>). You could never assign a variable inside an <code>if</code> statement,
                    unlike in C or Java. This was by design, to prevent the common bug of typing <code>if (x = 5)</code> instead of <code>if (x == 5)</code>.
                </p>
                <p>
                    The Walrus operator breaks this rule selectively. It allows you to assign a value to a variable <strong>and</strong>
                    return that value strictly within an expression.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Syntax:</strong> Usage rules, parentheses, and precedence.</li>
                        <li><strong>The "Loop-and-a-Half":</strong> solving the classic file reading problem.</li>
                        <li><strong>Scope Leakage:</strong> The critical (and dangerous) difference from list comprehensions.</li>
                        <li><strong>The "Witness" Pattern:</strong> Using <code>any()</code> to capture data.</li>
                        <li><strong>Accumulators:</strong> Running totals inside comprehensions.</li>
                        <li><strong>Performance:</strong> How it reduces redundant function calls in bytecode.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Mental Model: "The Scratchpad"</h2>
                <p>
                    The easiest way to understand the Walrus is to think of it as a "Scratchpad".
                    Imagine you are doing a complex calculation in your head. Sometimes, you need to write down an intermediate result
                    to use it immediately in the next step.
                </p>

                <div className="analogy-box">
                    <h3> Analogy: The Memo Pad</h3>
                    <p>
                        <strong>Standard Assignment (<code>=</code>)</strong> is like writing a final answer in a ledger.
                        It is a standalone action. You stop what you are doing to write it down.
                    </p>
                    <p>
                        <strong>Walrus Assignment (<code>:=</code>)</strong> is like scribbling a number on a sticky note while
                        you are still talking. You record the value <em>and</em> keep using it in the conversation flow.
                    </p>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Statement vs Expression</span>
                    </div>
                    <CodeBlock language="python" code={`# Standard Assignment (Statement)
# You CANNOT put this inside print() or if()
x = 5
print(x)

# Walrus Assignment (Expression)
# You CAN put this anywhere an expression is expected
print(x := 5)  # Prints 5, AND assigns 5 to x
print(x)       # Prints 5 again

#  Syntax Error
# if (x = 5) &gt; 0: ...

# Allowed
if (x := 5) &gt; 0:
    print("Assigned and checked in one step!")`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Golden Use Case: "Loop-and-a-Half"</h2>
                <p>
                    The most compelling argument for the Walrus was reading files or data streams.
                    Before Python 3.8, reading chunks of data required code duplication or infinite loops with <code>break</code>.
                </p>

                <div className="comparison-table">
                    <h3>Evolution of the Read Loop</h3>
                    <table style={{ width: '100%', tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '15%' }}>Era</th>
                                <th style={{ width: '50%' }}>Pattern</th>
                                <th style={{ width: '35%' }}>Critique</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Legacy</strong></td>
                                <td>
                                    <CodeBlock language="python" code={`line = f.readline()
while line:
    process(line)
    line = f.readline()`} />
                                </td>
                                <td><strong>Repeated Code:</strong> Violation of DRY (Don't Repeat Yourself). The read call must happen twice.</td>
                            </tr>
                            <tr>
                                <td><strong>Hack</strong></td>
                                <td>
                                    <CodeBlock language="python" code={`while True:
    line = f.readline()
    if not line: break
    process(line)`} />
                                </td>
                                <td><strong>Boilerplate:</strong> Infinite loop + manual break is harder to read and error-prone.</td>
                            </tr>
                            <tr>
                                <td><strong>Modern</strong></td>
                                <td>
                                    <CodeBlock language="python" code={`while (line := f.readline()):
    process(line)`} />
                                </td>
                                <td><strong>Perfection:</strong> Concise, readable, and directly expresses the intent.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Deep Dive: Optimizing Comprehensions</h2>
                <p>
                    Data Scientists love List Comprehensions. But what if you need to filter a list based on an expensive calculation?
                    Pre-Walrus, you had to calculate it twice or expand the comprehension into a full loop.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Avoiding Redundant Logic</span>
                    </div>
                    <CodeBlock language="python" code={`import math

data = [1.5, 2.5, 3.5, 4.5]

def complex_math(x):
    # Imagine this takes 1 second to run
    return math.sin(x) * math.exp(x)

#  The Slow Way
# complex_math(x) runs TWICE per item!
result = [
    complex_math(x) 
    for x in data 
    if complex_math(x) &gt; 0
]

# The Walrus Way
# Runs once. Captures result in 'y'. Checks 'y'. Uses 'y'.
result = [
    y 
    for x in data 
    if (y := complex_math(x)) &gt; 0
]`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Performance Win:</strong> This isn't just syntactic sugar. In the example above, if <code>complex_math</code> is slow,
                        the Walrus version is literally <strong>2x faster</strong>.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Critical Concept: Scope Leakage</h2>
                <p>
                    This is where the Walrus bites back. In a standard List Comprehension, the loop variable (<code>for x in...</code>)
                    is local to the comprehension. It does not exist after the list is built.
                </p>
                <p>
                    <strong>Walrus variables LEAK.</strong> They are scoped to the function (or module) where they are defined, NOT the comprehension.
                </p>

                <CodeBlock language="python" code={`# 1. Standard Comprehension
[i for i in range(5)]
# print(i)  # NameError: name 'i' is not defined. (Clean!)

# 2. Walrus Comprehension
[squared := x**2 for x in range(5)]
print(squared)  # Outputs: 16 (The last value persists!)

# Why does this matter?
# If you use a variable name that already exists, the Walrus will overwrite it!
total = 100
[total := x for x in range(5)]
print(total)  # Outputs: 4 (Your original 100 is GONE)`} />
            </div>

            <div className="content-section">
                <h2>Advanced Patterns</h2>
                <p>
                    Once you master the basics, the Walrus unlocks functional programming patterns that were previously impossible in Python.
                </p>

                <h3>1. The "Witness" Pattern with any()</h3>
                <p>
                    The <code>any()</code> function checks if <em>any</em> item in a list is True. But usually, you also want to know <em>which</em> item it was.
                    Before Walrus, you couldn't get the item out of <code>any()</code>.
                </p>
                <CodeBlock language="python" code={`numbers = [1, 4, 9, 105, 20]

# Goal: Find the first number &gt; 100
# any() normally just returns True/False

if any((val := x) &gt; 100 for x in numbers):
    print(f"Found a big number: {val}")
    # Output: Found a big number: 105

# How it works:
# 'val' updates on every check.
# any() stops immediately (short-circuits) when it finds a match.
# 'val' holds the value of 'x' that triggered the True.`} />

                <h3>2. The "Accumulator" (Running Limit)</h3>
                <p>
                    You can maintain state inside a comprehension. This allows for things like "Cumulative Sum" in a single line.
                </p>
                <CodeBlock language="python" code={`data = [1, 2, 3, 4]
total = 0

# Calculate running totals
running = [total := total + x for x in data]
print(running)
# Output: [1, 3, 6, 10]`} />

                <h3>3. Regex Switch-Case</h3>
                <p>
                    Before <code>match-case</code> (Python 3.10), this was the cleanest way to handle multiple Regex patterns.
                </p>
                <CodeBlock language="python" code={`import re
command = "resize 1024x768"

if match := re.search(r"resize (\d+)x(\d+)", command):
    print(f"Resizing to {match.group(1)} by {match.group(2)}")
elif match := re.search(r"quit", command):
    print("Quitting app")
elif match := re.search(r"help", command):
    print("Show help")`} />
            </div>

            <div className="content-section">
                <h2>Performance Lab: Bytecode Analysis</h2>
                <p>
                    Is the Walrus faster? Let's ask the <code>dis</code> (disassembler) module.
                </p>
                <p>
                    In a standard loop, Python does a <code>LOAD_FAST</code> (variable lookup) and <code>STORE_FAST</code> (assignment).
                    The Walrus operator essentially combines these operations in the stack, but maintains similar bytecode instructions via <code>DUP_TOP</code> (Duplicating the value on stack to use it and store it).
                </p>
                <p>
                    <strong>Verdict:</strong> For simple assignments (<code>x = 5</code>), Walrus is not faster. Its speed comes from <strong>structural optimization</strong>avoiding a second call to a slow function (like <code>f.readline()</code> or <code>re.search()</code>).
                </p>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices & "Walrus Abuse"</h2>
                <p>
                    The Walrus is powerful, but power corrupts. Code readability is the priority.
                </p>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use in <code>if</code> / <code>while</code> conditions to reduce function calls.</li>
                            <li>Use in List Comprehensions to avoid recalculating values.</li>
                            <li>Use for concise Regex matching blocks.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't use it for simple assignments: <code>if (x := 5) == 5:</code> is just clutter.</li>
                            <li>Don't nest them: <code>if (x := (y := z + 1))</code> is asking for bugs.</li>
                            <li>Don't ignore the scope leak: Use distinct variable names inside comprehensions.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonWalrus;


